import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { useRequireRole } from "@/lib/use-require-role";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { clientForPortalUser, portalDataForClient } from "@/mocks/saas-data";
import { fmtUsd, type Invoice } from "@/mocks/data";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/portal/invoices")({
  ssr: false,
  head: () => ({ meta: [{ title: "Portal — Invoices" }] }),
  component: Page,
});

function Page() {
  const user = useRequireRole(["client_portal"]);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const [tick, setTick] = useState(0);
  if (!user) return null;
  const client = clientForPortalUser(user.id);
  const invoices = client ? portalDataForClient(client.name).invoices : [];

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 lg:px-10 lg:py-10">
      <PageHeader title="Invoices" description="View and pay your invoices." />
      <div className="overflow-hidden rounded-xl bg-card ring-1 ring-border">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-surface-muted/50">
            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-medium">Invoice</th>
              <th className="px-4 py-3 font-medium">Issued</th>
              <th className="px-4 py-3 font-medium">Due</th>
              <th className="px-4 py-3 font-medium">Amount</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {invoices.map((i) => (
              <tr key={i.id}>
                <td className="px-4 py-3 font-mono text-xs">{i.number}</td>
                <td className="px-4 py-3 text-muted-foreground">{i.issued}</td>
                <td className="px-4 py-3 text-muted-foreground">{i.due}</td>
                <td className="px-4 py-3 font-mono font-medium">{fmtUsd(i.amount)}</td>
                <td className="px-4 py-3"><StatusBadge status={i.status} /></td>
                <td className="px-4 py-3 text-right">
                  {(i.status === "sent" || i.status === "overdue") && (
                    <Button size="sm" onClick={() => setPaying(i)}>Pay now</Button>
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No invoices yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <PayDialog
        invoice={paying}
        onClose={() => setPaying(null)}
        onPaid={(inv) => {
          inv.status = "paid";
          setPaying(null);
          toast.success(`${inv.number} paid`);
          setTick((t) => t + 1);
        }}
      />
      <span className="hidden">{tick}</span>
    </div>
  );
}

function PayDialog({ invoice, onClose, onPaid }: { invoice: Invoice | null; onClose: () => void; onPaid: (i: Invoice) => void }) {
  const [card, setCard] = useState("4242 4242 4242 4242");
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pay {invoice?.number}</DialogTitle>
        </DialogHeader>
        {invoice && (
          <div className="space-y-3">
            <div className="rounded-md bg-surface-muted/40 p-4 text-center">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Total</p>
              <p className="mt-1 font-mono text-2xl font-semibold">{fmtUsd(invoice.amount)}</p>
            </div>
            <Label>Card number</Label>
            <Input value={card} onChange={(e) => setCard(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Mock payment — no charge.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => invoice && onPaid(invoice)}>Pay {invoice ? fmtUsd(invoice.amount) : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
